/**
 * Configuración centralizada de la API
 * 
 * Esta configuración garantiza que las llamadas a la API funcionen correctamente en:
 * - Web (desarrollo/producción) - Usa REACT_APP_BACKEND_URL o URL relativa
 * - Android (Capacitor) - Usa URL de producción
 * - iOS (Capacitor) - Usa URL de producción
 * 
 * En Capacitor, las rutas relativas no funcionan porque el origen es capacitor://localhost,
 * por lo que todas las llamadas deben usar URLs absolutas.
 */

// URL de producción para apps móviles (Android/iOS)
const PRODUCTION_API_URL = 'https://www.datacultivo.com';

// Detectar si estamos en Capacitor (Android/iOS)
// Múltiples métodos de detección para mayor robustez
export const isCapacitor = (() => {
  if (typeof window === 'undefined') return false;
  
  // Método 1: Protocolo capacitor://
  if (window.location?.protocol === 'capacitor:') return true;
  
  // Método 2: window.Capacitor existe y es plataforma nativa
  if (window.Capacitor?.isNativePlatform?.()) return true;
  
  // Método 3: User agent contiene Capacitor
  if (navigator?.userAgent?.includes('Capacitor')) return true;
  
  // Método 4: window.Capacitor.platform es 'android' o 'ios'
  if (window.Capacitor?.platform === 'android' || window.Capacitor?.platform === 'ios') return true;
  
  return false;
})();

// URL base de la API
// - En Capacitor (Android/iOS): usa URL de producción
// - En desarrollo web: usa REACT_APP_BACKEND_URL
// - En producción web: usa URL relativa (vacía)
export const API_BASE_URL = (() => {
  // En Capacitor siempre usar la URL de producción
  if (isCapacitor) {
    return process.env.REACT_APP_API_BASE || PRODUCTION_API_URL;
  }
  // En web, usar la variable de entorno o vacío para URLs relativas
  return process.env.REACT_APP_BACKEND_URL || '';
})();

// Log para debugging
if (typeof window !== 'undefined') {
  console.log('[API Config] isCapacitor:', isCapacitor);
  console.log('[API Config] API_BASE_URL:', API_BASE_URL);
  if (window.Capacitor) {
    console.log('[API Config] Capacitor.platform:', window.Capacitor.platform);
  }
}

// Usar proxy solo en Capacitor (en web las llamadas directas funcionan)
export const USE_PROXY_FOR_EXTERNAL = isCapacitor;

// Endpoints específicos
export const API_ENDPOINTS = {
  // Proxy CKAN para datos del Cabildo de Tenerife
  CKAN_PROXY: `${API_BASE_URL}/api/ckan-proxy`,
  CKAN: `${API_BASE_URL}/api/ckan`,
  CKAN_RESOURCE_FILE: `${API_BASE_URL}/api/ckan/resource_file`,
  
  // API Meteorológica (Agroclima)
  METEO: `${API_BASE_URL}/api/meteo`,
  METEO_STATIONS: `${API_BASE_URL}/api/meteo/stations`,
};

// Opciones por defecto para fetch
export const DEFAULT_FETCH_OPTIONS = {
  headers: {
    'Accept': 'application/json',
  },
  mode: 'cors',
};

/**
 * Función para obtener una URL externa
 * - En web: devuelve la URL directa (CKAN tiene CORS configurado)
 * - En Capacitor: usa el proxy del backend
 * @param {string} externalUrl - URL externa (ej: https://datos.tenerife.es/...)
 * @param {string} format - Formato de respuesta ('json' o 'text')
 * @returns {string} - URL lista para usar
 */
export function getExternalUrl(externalUrl, format = 'json') {
  if (USE_PROXY_FOR_EXTERNAL) {
    const params = new URLSearchParams({ url: externalUrl });
    if (format !== 'json') {
      params.append('format', format);
    }
    return `${API_ENDPOINTS.CKAN_PROXY}?${params.toString()}`;
  }
  return externalUrl;
}

export default {
  API_BASE_URL,
  API_ENDPOINTS,
  DEFAULT_FETCH_OPTIONS,
  USE_PROXY_FOR_EXTERNAL,
  isCapacitor,
  getExternalUrl,
};
